import React, { useEffect, useMemo, useState, useRef } from 'react';
import { css, useTheme } from '@emotion/react';
import { BirdiTheme, breakpoints } from '@birdi/theme';
import { useNavigate } from 'react-router';
import { maps as mapsAPI, stroot } from '@birdi/js-sdk';
import type { Map as MapType } from '@birdi/js-sdk/src/public/maps';
import type { MostViewedMap } from '@birdi/js-sdk/src/public/maps';
import { useMetaSelector } from '../../meta/reducer';
import { posthog } from '../../lib/posthog';
import { getRelativeTime } from '../utils';
import ChevronDownSVG from '@birdi/icons/chevron-down.svg';
import EyeSVG from '@birdi/icons/eye.svg';
import MessageSquareSVG from '@birdi/icons/message-square.svg';
import StarSVG from '@birdi/icons/star.svg';
import type { WidgetProps } from '../tabs';
import { ReorderingIcon } from './reordering-icon';
import { useMapCovers } from '../use-map-covers';

const MAPS_LIMIT = 10;
const DISPLAY_LIMIT = 4;
const DEFAULT_COVER =
  'https://birdi-public.s3.ap-southeast-2.amazonaws.com/street-background.png';

type TimePeriod = 'all' | 'last_30_days' | 'last_7_days' | 'today';

const TIME_PERIOD_OPTIONS: { value: TimePeriod; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'last_30_days', label: 'Last 30 days' },
  { value: 'last_7_days', label: 'Last 7 days' },
  { value: 'today', label: 'Today' },
];

const getDateRange = (
  period: TimePeriod,
): { startDate?: string; endDate?: string } => {
  if (period === 'all') return {};
  const now = new Date();
  const endDate = now.toISOString();
  let startDate: Date;
  switch (period) {
    case 'today':
      startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      break;
    case 'last_7_days':
      startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      break;
    case 'last_30_days':
    default:
      startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
      break;
  }
  return { startDate: startDate.toISOString(), endDate };
};

function formatViewCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

export const MostViewedMaps: React.FC<WidgetProps> = ({
  isReorderMode,
  isHovered,
  isDragging,
  suppressIntercomAnchor,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const meta = useMetaSelector();

  const [maps, setMaps] = useState<MostViewedMap[]>([]);
  const [loading, setLoading] = useState(true);
  const [timePeriod, setTimePeriod] = useState<TimePeriod>('all');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const mapsForCovers = useMemo<MapType[]>(
    () => maps.map((m) => ({ id: m.mapId } as MapType)),
    [maps],
  );
  const { mapCovers } = useMapCovers(mapsForCovers);

  const displayedMaps = maps.slice(0, expanded ? maps.length : DISPLAY_LIMIT);
  const hasData = !loading && maps.length > 0;
  const hasMore = !expanded && maps.length >= DISPLAY_LIMIT;
  const isExpandDisabled = !hasData || !hasMore || loading;

  const workspaceId = meta.workspace?.id || meta.organisation.id;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!meta.organisation?.id || !workspaceId) {
      setMaps([]);
      setLoading(false);
      return;
    }
    const { startDate, endDate } = getDateRange(timePeriod);
    const limit = expanded ? MAPS_LIMIT : DISPLAY_LIMIT;
    const opts: { limit: number; startDate?: string; endDate?: string } = {
      limit,
    };
    if (startDate) opts.startDate = startDate;
    if (endDate) opts.endDate = endDate;

    (async () => {
      setLoading(true);
      try {
        const res = await mapsAPI.getMostViewedMaps(
          stroot('mvmap1'),
          workspaceId,
          opts,
        );
        setMaps(res.body ?? []);
      } catch (e) {
        console.error('Error fetching most viewed maps:', e);
        setMaps([]);
      }
      setLoading(false);
    })();
  }, [meta.organisation?.id, workspaceId, timePeriod, expanded]);

  const handleTimePeriodChange = (period: TimePeriod) => {
    posthog?.capture('dashboard most viewed maps time period changed', {
      period,
    });
    setTimePeriod(period);
    setDropdownOpen(false);
  };

  const handleMapClick = (mapId: string) => {
    posthog?.capture('dashboard most viewed maps clicked', { mapId });
    navigate(`/map/next/${mapId}`);
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        border-radius: 1em;
        box-shadow: 2px 2px 2px ${theme.mono30};
        background: ${theme.mono0};
        min-height: 320px;
        min-width: 0;
        max-width: 100%;
        @media (max-width: ${breakpoints.s}) {
          min-height: 220px;
        }
        @media (max-width: ${breakpoints.xs}) {
          min-height: 180px;
          width: 100%;
        }
      `}
      id={suppressIntercomAnchor ? undefined : 'most-viewed-maps'}
    >
      {/* Header: title + reorder */}
      <div
        css={css`
          color: ${theme.midnight};
          font-weight: 700;
          background: ${theme.lightOcean};
          padding: 1em;
          border-top-left-radius: 1em;
          border-top-right-radius: 1em;
          display: flex;
          align-items: center;
          gap: 0.5em;
        `}
      >
        <ReorderingIcon
          isReorderMode={isReorderMode}
          isHovered={isHovered}
          isDragging={isDragging}
        />
        <span>Most viewed maps</span>
      </div>

      <div
        css={css`
          flex: 1 1 auto;
          padding: 1em;
          color: ${theme.mono50};
          display: flex;
          flex-direction: column;
        `}
      >
        {/* Subtitle row: "Most active maps" + dropdown (only when has data) */}
        <div
          css={css`
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 1em;
          `}
        >
          <span>Most active maps</span>
          {hasData && (
            <div ref={dropdownRef} css={css`position: relative;`}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 0.5em;
                  background: ${theme.mono0};
                  border: 1px solid ${theme.mono30};
                  border-radius: 6px;
                  padding: 0.4em 0.75em;
                  cursor: pointer;
                  color: ${theme.midnight};
                  font-size: 0.9em;
                  white-space: nowrap;
                  &:hover {
                    background: ${theme.mono10};
                  }
                `}
              >
                <span>
                  {TIME_PERIOD_OPTIONS.find((o) => o.value === timePeriod)
                    ?.label}
                </span>
                <span
                  css={css`
                    display: flex;
                    width: 14px;
                    height: 14px;
                    transform: ${dropdownOpen
                      ? 'rotate(180deg)'
                      : 'rotate(0deg)'};
                    transition: transform 0.2s;
                  `}
                >
                  <ChevronDownSVG />
                </span>
              </button>
              {dropdownOpen && (
                <div
                  css={css`
                    position: absolute;
                    top: 100%;
                    right: 0;
                    margin-top: 4px;
                    background: ${theme.mono0};
                    border: 1px solid ${theme.mono30};
                    border-radius: 6px;
                    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
                    z-index: 10;
                    min-width: 140px;
                  `}
                >
                  {TIME_PERIOD_OPTIONS.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => handleTimePeriodChange(option.value)}
                      css={css`
                        display: block;
                        width: 100%;
                        padding: 0.5em 0.75em;
                        background: ${timePeriod === option.value
                          ? theme.mono10
                          : 'transparent'};
                        border: none;
                        text-align: left;
                        cursor: pointer;
                        color: ${theme.midnight};
                        font-size: 0.9em;
                        &:hover {
                          background: ${theme.mono10};
                        }
                        &:first-of-type {
                          border-radius: 6px 6px 0 0;
                        }
                        &:last-of-type {
                          border-radius: 0 0 6px 6px;
                        }
                      `}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Empty state */}
        {!loading && maps.length === 0 && (
          <div
            css={css`
              height: 13em;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: flex-start;
            `}
          >
            <div css={css`color: ${theme.mono60};`}>
              <strong>Your most active maps will appear here.</strong>
            </div>
            <div
              css={css`
                margin-top: 0.5em;
                color: ${theme.mono50};
              `}
            >
              Open and interact with your maps to start seeing activity trends
              in this space.
            </div>
          </div>
        )}

        {/* Filled state: list with thumbnail, title, Updated X ago, Top badge, stats */}
        {hasData && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              gap: 0.5em;
            `}
          >
            {displayedMaps.map((item, index) => (
              <div
                key={item.mapId}
                onClick={() => handleMapClick(item.mapId)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleMapClick(item.mapId);
                  }
                }}
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 1em;
                  background: ${theme.mono0};
                  border-radius: 8px;
                  border: 1px solid ${theme.mono30};
                  padding: 0.75em 1em;
                  cursor: pointer;
                  transition: box-shadow 0.15s, background 0.15s;
                  &:hover {
                    background: ${theme.mono20};
                    border-color: ${theme.mono20};
                  }
                `}
              >
                <div
                  css={css`
                    flex-shrink: 0;
                    width: 5em;
                    height: 5em;
                    border-radius: 5px;
                    overflow: hidden;
                    background: ${theme.mono20};
                  `}
                >
                  <img
                    src={mapCovers[item.mapId] || DEFAULT_COVER}
                    alt=""
                    css={css`
                      width: 100%;
                      height: 100%;
                      object-fit: cover;
                    `}
                  />
                </div>
                <div
                  css={css`
                    flex: 1;
                    min-width: 0;
                  `}
                >
                  <div
                    css={css`
                      font-weight: 700;
                      color: ${theme.midnight};
                      font-size: 1em;
                    `}
                  >
                    {item.mapName || 'Untitled Map'}
                  </div>
                  <div
                    css={css`
                      color: ${theme.mono50};
                      font-size: 0.85em;
                      margin-top: 0.2em;
                    `}
                  >
                    {item.updatedAt
                      ? `Updated ${getRelativeTime(item.updatedAt)}`
                      : '—'}
                  </div>
                  {index === 0 && (
                    <div
                      css={css`
                        display: inline-flex;
                        align-items: center;
                        gap: 0.25em;
                        margin-top: 0.35em;
                        padding: 0.15em 0.5em;
                        border-radius: 4px;
                        background: #FFF4D5;
                        color: #E6A100;
                        font-size: 0.75em;
                        font-weight: 600;
                      `}
                    >
                      <span
                        css={css`
                          display: flex;
                          width: 12px;
                          height: 12px;
                        `}
                      >
                        <StarSVG />
                      </span>
                      Top
                    </div>
                  )}
                </div>
                {/* Column 2: icons (right-aligned) */}
                <div
                  css={css`
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-end;
                    gap: 0.35em;
                  `}
                >
                  <span
                    css={css`
                      display: flex;
                      width: 16px;
                      height: 16px;
                      color: ${theme.mono50};
                    `}
                  >
                    <EyeSVG />
                  </span>
                  <span
                    css={css`
                      display: flex;
                      width: 16px;
                      height: 16px;
                      color: ${theme.mono50};
                    `}
                  >
                    <MessageSquareSVG />
                  </span>
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      position: relative;
                      height: 28px;
                    `}
                  >
                    {item.collaborators
                      .slice(0, 3)
                      .map((collab, i) => (
                        <div
                          key={i}
                          css={css`
                            width: 28px;
                            height: 28px;
                            border-radius: 50%;
                            background: ${theme.lightOcean};
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            font-weight: 700;
                            font-size: 0.7em;
                            margin-left: ${i === 0 ? 0 : -16}px;
                            border: 2px solid ${theme.mono0};
                            overflow: hidden;
                            color: ${theme.deepOcean};
                          `}
                        >
                          {collab.profilePic ? (
                            <img
                              src={collab.profilePic}
                              alt=""
                              css={css`
                                width: 100%;
                                height: 100%;
                                object-fit: cover;
                              `}
                            />
                          ) : (
                            <span>
                              {collab.fname?.[0] ?? ''}
                              {collab.lname?.[0] ?? ''}
                            </span>
                          )}
                        </div>
                      ))}
                    {item.collaboratorCount > 3 && (
                      <div
                        css={css`
                          margin-left: -16px;
                          width: 28px;
                          height: 28px;
                          border-radius: 50%;
                          background: ${theme.mono0};
                          color: ${theme.flora};
                          font-size: 0.7em;
                          font-weight: 700;
                          display: flex;
                          align-items: center;
                          justify-content: center;
                          border: 2px solid ${theme.flora};
                        `}
                      >
                        +{item.collaboratorCount - 3}
                      </div>
                    )}
                  </div>
                </div>
                {/* Column 3: numbers (left-aligned) */}
                <div
                  css={css`
                    flex-shrink: 0;
                    display: flex;
                    flex-direction: column;
                    align-items: flex-start;
                    gap: 0.35em;
                    font-size: 0.85em;
                    color: ${theme.mono50};
                  `}
                >
                  <span>
                    {formatViewCount(item.viewCount)} views
                  </span>
                  <span>{item.commentCount.toLocaleString()} comments</span>
                  <span>
                    {item.collaboratorCount.toLocaleString()} collaborators
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {loading && (
          <div
            css={css`
              text-align: center;
              margin: 1em 0;
              color: ${theme.mono50};
            `}
          >
            Loading...
          </div>
        )}
      </div>

      {/* Expand button */}
      <div
        css={css`
          padding: 1em;
          border-top: 1px solid ${theme.mono20};
        `}
      >
        <button
          disabled={isExpandDisabled}
          onClick={() => setExpanded((e) => !e)}
          css={css`
            width: 100%;
            color: ${isExpandDisabled ? theme.mono40 : theme.mono60};
            border: solid 1px ${theme.mono30};
            border-radius: 6px;
            padding: 0.5em 1em;
            font-weight: 500;
            cursor: ${isExpandDisabled ? 'not-allowed' : 'pointer'};
            background: ${theme.mono0};
            opacity: ${isExpandDisabled ? 0.6 : 1};
          `}
        >
          Expand
        </button>
      </div>
    </div>
  );
};
